import { DynamicFormConfig, ProvideDynamicForms } from '@teamfoster/dynamic-forms';
import { CookieConfig, ProvideCookiesNgrx } from '@teamfoster/sdk/cookie-ngrx';
import { DictionaryConfig, ProvideDictionaryNgrx } from '@teamfoster/sdk/dictionary-ngrx';
import { formControls } from './form/form.providers';
import { provideStore, Store } from '@ngrx/store';
import { getActiveLanguageNoCulture, getLanguageState, ProvideLanguageNgrx } from '@teamfoster/sdk/language-ngrx';
import { ApplicationConfig, LOCALE_ID } from '@angular/core';
import { routes } from './app.routes';
import { provideRouter, withComponentInputBinding, withInMemoryScrolling } from '@angular/router';
import { appEffects, CustomSerializer, metaReducers, reducers } from './store';
import { provideEffects } from '@ngrx/effects';
import { provideRouterStore } from '@ngrx/router-store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { environment } from 'src/environments/environment';
import { ProvideMenuNgrx } from '@teamfoster/sdk/menu-ngrx';
import { ProvideSocialMedia } from './social-media/social-media.providers';
import { ProvideStorefronts } from './storefronts';
import { ProvideFaq } from './faq/faq.providers';
import { ProvideNewsletters } from './newsletters/newsletters.providers';
import { ProvideShop } from './shop/shop.providers';
import { ProvideActionBars } from './action-bars/action-bars.providers';
import { ImageSharpConfig, ProvideOptimisedImageProcessor } from '@teamfoster/sdk/image';
import { ProvideIcons } from '@teamfoster/sdk/icon';
import { HTTP_INTERCEPTORS, provideHttpClient, withFetch, withInterceptorsFromDi } from '@angular/common/http';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MAT_SNACK_BAR_DEFAULT_OPTIONS } from '@angular/material/snack-bar';
import { provideClientHydration, withIncrementalHydration } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { LanguageInterceptor } from './utility/language.interceptor';
import { ProvideThemes } from './themes/themes.providers';
import { BASE_URL } from '@teamfoster/sdk/ssr';

const dictConfig: Partial<DictionaryConfig> = {
  mapping: [
    {
      domainPart: '.nl/',
      language: 'nl',
    },
  ],
};

const languageSettings = {
  defaultLanguage: 'nl-NL',
  mapping: [
    { domainPart: '.nl/', language: 'nl-NL' },
    { domainPart: '.de/', language: 'de-DE' },
    { domainPart: '.com/', language: 'en-GB' },
    { domainPart: '.fr/', language: 'en-GB' },
    { domainPart: '.co.uk/', language: 'en-GB' },
  ],
};

const cookieConfig: CookieConfig = {
  analyticsCode: 'GTM-QXR9',
  useTagManager: true,
  strippedContentPlaceholder: 'Deze video wordt niet getoond omdat je geen marketing cookies hebt geaccepteerd',
  buttonSettings: 'Instellingen',
  buttonAcceptAll: 'Accept all',
  buttonClose: 'Sluiten',
  strippedContentButton: 'Cookie-instellingen wijzigen',
  types: {
    analytical: true,
    functional: true,
    marketing: true,
  },
  showSettings: true,
};

export const dynamicFormConfig: DynamicFormConfig = {
  genericInputErrorMessage: 'Het formulier is niet correct ingevuld, controleer de velden en probeer nogmaals.',
  genericServerErrorMessage:
    'Er is iets fout gegaan bij het versturen van het formulier, probeer het later nogmaals. Blijft het probleem voordoen neem dan contact met ons op.',
  formControls: formControls,
  formControlErrors: {
    pattern: 'Telefoonnummer-error',
    passwordInvalid: 'password-invalid-error',
    // VatNumberInvalid: 'test',
  },
};

const getLocaleId = (baseUrl: string) => {
  if (languageSettings.mapping && baseUrl) {
    const domain = languageSettings.mapping.find(a => baseUrl?.indexOf(a.domainPart) >= 0);

    if (domain) {
      return domain!.language;
    }
  }
  return languageSettings.defaultLanguage;
};

export const appConfig: ApplicationConfig = {
  providers: [
    provideAnimations(),
    provideRouter(
      routes,
      withComponentInputBinding(),
      withInMemoryScrolling({ scrollPositionRestoration: 'disabled', anchorScrolling: 'enabled' })
    ),
    provideStore(reducers, {
      metaReducers,
      runtimeChecks: {
        strictStateImmutability: true,
        strictActionImmutability: true,
      },
    }),
    provideEffects(appEffects),
    provideRouterStore({ serializer: CustomSerializer }),
    provideStoreDevtools({ maxAge: 25, logOnly: environment.production }),
    ProvideDictionaryNgrx(dictConfig),
    ProvideStorefronts(),
    ProvideFaq(),
    ProvideNewsletters(),
    ProvideShop(),
    ProvideActionBars(),
    ProvideThemes(),
    ProvideMenuNgrx({}),
    ProvideSocialMedia(),
    ProvideLanguageNgrx(languageSettings),
    ProvideIcons({ spritesheetUrl: 'assets/dist/svg/symbol/svg/sprite.symbol.svg' }),
    ProvideCookiesNgrx(cookieConfig),
    ProvideOptimisedImageProcessor(ImageSharpConfig, {
      defaultFormat: 'webp',
    }),
    ProvideDynamicForms(dynamicFormConfig),
    provideHttpClient(withInterceptorsFromDi(), withFetch()),
    provideClientHydration(withIncrementalHydration()),
    { provide: LOCALE_ID, useFactory: (baseUrl: string) => getLocaleId(baseUrl), deps: [BASE_URL] },
    { provide: HTTP_INTERCEPTORS, useClass: LanguageInterceptor, multi: true },
    { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { appearance: 'fill', floatLabel: 'always' } },
    { provide: MAT_SNACK_BAR_DEFAULT_OPTIONS, useValue: { duration: 1500 } },
  ],
};
